// src/ecrans/voyageurs/EcranVoyageurs.jsx — VOYAGEURS NEST (maquette v677,
// « Admin › Voyageurs », relevés 250 à 257).
//
// `EnteteVoyageurs` : quatre tuiles et quatre sous-onglets.
// `VueMessages` : la liste des conversations (filtres, logement, recherche)
// et, à droite, l'aperçu du fil choisi.
//
// Lu SEULEMENT par les portes : ecran_messages_compteurs,
// ecran_conversations_lire, ecran_logements_lire, ecran_fil_lire (le fil,
// sa dernière page), /api/sites site_booking_requests_list et avis_admin_list.
// « Ouvrir la conversation » passe par l'écran de réponse existant, qui porte
// l'envoi (écriture, hors lot).

import { useEffect, useMemo, useState } from "react";
import { useLecture, useLectureSites, nombreDe } from "../portes";
import { StyleNest, EnTete, Tuile, Bascule, EtatLecture, ICO, C, PLATEFORMES, aujourdhuiParis, pl } from "../nest";
import { filtrerFils, quand, ligneSejour, initiales, lettrePlateforme, lundiDe } from "./voyageurs-calculs";

const ICONES_VOY = { messages: ICO.msg, "voyageurs-textes": ICO.regles, "voyageurs-demandes": ICO.etoile, "voyageurs-avis": ICO.etoile };
export const VOYAGEURS_ONGLETS = {
  messages: "Messages",
  "voyageurs-textes": "Automatisations",
  "voyageurs-demandes": "Demandes",
  "voyageurs-avis": "Avis",
};

const CSS = `
.nvoy-barre{display:flex;gap:8px;flex-wrap:wrap;align-items:center;background:#fff;padding:10px 12px;border-bottom:1px solid ${C.bord}}
.nvoy-g{display:grid;grid-template-columns:290px minmax(0,1fr);gap:0;background:#fff;border:1px solid ${C.bord};border-radius:14px;overflow:hidden}
.nvoy-liste{padding:10px;border-right:1px solid ${C.bord};max-height:900px;overflow-y:auto}
.nvoy-f{all:unset;box-sizing:border-box;cursor:pointer;display:flex;gap:10px;width:100%;border:1px solid ${C.bord};border-radius:12px;padding:10px;margin-bottom:6px;background:#fff}
.nvoy-f.on{border-color:${C.encre};box-shadow:0 0 0 1px ${C.encre}}
.nvoy-av{position:relative;width:36px;height:36px;border-radius:50%;background:#E3EAE6;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#1C3A33;flex-shrink:0}
.nvoy-av i{position:absolute;right:-3px;bottom:-3px;width:16px;height:16px;border-radius:50%;font-style:normal;font-size:9px;font-weight:700;color:#fff;display:flex;align-items:center;justify-content:center;border:2px solid #fff}
.nvoy-l1{display:flex;align-items:center;gap:6px}
.nvoy-l1 b{font-size:13px;font-weight:700;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.nvoy-l1 .d{font-size:11px;color:${C.gris}}
.nvoy-s{font-size:11px;color:${C.gris};margin-top:2px}
.nvoy-urg{font-size:9.5px;font-weight:700;background:${C.aub};color:#fff;border-radius:9px;padding:1px 7px}
.nvoy-droite{padding:14px 16px;min-width:0}
.nvoy-bulle{max-width:80%;border-radius:12px;padding:9px 12px;font-size:12.5px;line-height:1.5;margin-bottom:8px}
@media (max-width:760px){.nvoy-g{grid-template-columns:1fr}.nvoy-liste{border-right:none;max-height:none}}
`;

/** L'en-tête de Voyageurs (quatre tuiles) et sa barre de sous-onglets. */
/** Une ligne dépliée sous une tuile : toucher emmène au bon endroit, déjà ouvert. */
function LigneDepliee({ titre, sous, onClick, testId }) {
  return (
    <button type="button" className="nest-ligne" onClick={onClick} data-testid={testId}>
      <span className="co">
        <span className="t">{titre}</span>
        <span className="s">{sous}</span>
      </span>
      <span className="ch" aria-hidden="true">›</span>
    </button>
  );
}

export function EnteteVoyageurs({ tab, onTab, onDestination }) {
  // Un seul compteur déplié à la fois ; second appui replie (comme l'ancien en-tête).
  const [deplie, setDeplie] = useState(null);
  const basculer = (k) => setDeplie((d) => (d === k ? null : k));
  const aller = (t, dest) => {
    if (onDestination) onDestination(dest);
    setDeplie(null);
    onTab(t);
  };
  const today = useMemo(() => aujourdhuiParis(), []);
  const [compteurs] = useLecture("ecran_messages_compteurs", {});
  const demandes = useLectureSites("site_booking_requests_list", { statut: "pending", limit: "50", offset: "0" });
  const avis = useLectureSites("avis_admin_list", { page: "0" });
  const lundi = lundiDe(today);
  const nDemandes = nombreDe(demandes, (d) => Number(d.pending_count ?? (d.requests || []).length));
  const nAttente = nombreDe(compteurs, (d) => d.en_attente);
  const nAvis = nombreDe(avis, (d) =>
    (d.avis || []).filter((a) => String(a.created_at || a.review_date || "").slice(0, 10) >= lundi).length,
  );
  const nSans = nombreDe(compteurs, (d) => d.sans_reponse_3j);
  // Les listes des compteurs « messages » ne se lisent qu'au dépliage.
  const filtreFils = deplie === "messages" ? "en_attente" : deplie === "sans" ? "sans_reponse_3j" : null;
  const [filsDeplies, relireFils] = useLecture(filtreFils ? "ecran_conversations_lire" : null, { filtre: filtreFils || "tous" });
  const [logs] = useLecture(deplie ? "ecran_logements_lire" : null, {});
  const nomLog = (id) => (logs.etat === "ok" ? (logs.data.logements.find((l) => l.id === id) || {}).nom : null) || "Logement";
  const jj = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "");
  let corps = null;
  if (deplie === "demandes") {
    corps = demandes.etat !== "ok" ? <EtatLecture lecture={demandes} quoi="Demandes" /> : (demandes.data.requests || []).filter(Boolean).length
      ? demandes.data.requests.filter(Boolean).map((d) => (
          <LigneDepliee key={d.id} testId={`voy-dep-demande-${d.id}`} titre={`${d.guest_first_name || ""} ${d.guest_last_name || ""}`.trim() || "Voyageur"}
            sous={`${nomLog(d.logement_id)} · ${jj(d.start_date)} → ${jj(d.end_date)}`} onClick={() => aller("voyageurs-demandes", { type: "demande", id: d.id })} />
        ))
      : <p className="nest-etat">Aucune demande en attente.</p>;
  } else if (deplie === "avis") {
    const liste = avis.etat === "ok" ? (avis.data.avis || []).filter((a) => a && String(a.created_at || a.review_date || "").slice(0, 10) >= lundi) : [];
    corps = avis.etat !== "ok" ? <EtatLecture lecture={avis} quoi="Avis" /> : liste.length
      ? liste.map((a) => (
          <LigneDepliee key={a.id} testId={`voy-dep-avis-${a.id}`} titre={Number.isFinite(Number(a.rating)) && a.rating !== null ? `${Number(a.rating).toLocaleString("fr-FR")} sur 5` : "Sans note"}
            sous={[nomLog(a.logement_id), (a.review_text || "").slice(0, 64)].filter(Boolean).join(" · ")} onClick={() => aller("voyageurs-avis", { type: "avis", id: a.id })} />
        ))
      : <p className="nest-etat">Aucun avis depuis lundi.</p>;
  } else if (filtreFils) {
    const fils = filsDeplies.etat === "ok" ? filsDeplies.data.fils || [] : [];
    corps = filsDeplies.etat !== "ok" ? <EtatLecture lecture={filsDeplies} quoi="Conversations" onRelire={relireFils} /> : fils.length
      ? fils.map((f) => (
          <LigneDepliee key={f.thread_id} testId={`voy-dep-fil-${f.thread_id}`} titre={f.voyageur || "Voyageur"}
            sous={[f.logement ? f.logement.nom : null, f.extrait].filter(Boolean).join(" · ")} onClick={() => aller("messages", { type: "thread", id: f.thread_id })} />
        ))
      : <p className="nest-etat">{deplie === "sans" ? "Aucune conversation sans réponse depuis 3 jours." : "Aucun message n'attend de réponse."}</p>;
  }
  return (
    <div className="nest" data-testid="entete-voyageurs">
      <StyleNest />
      <style>{CSS}</style>
      <EnTete sous="Messages, automatisations, demandes et avis" titre="Voyageurs">
        <Tuile n={nDemandes} libelle="demandes en attente" ouverte={deplie === "demandes"} testId="voy-tuile-demandes" onClick={() => basculer("demandes")} />
        <Tuile n={nAttente} libelle="messages en attente" ouverte={deplie === "messages"} testId="voy-tuile-messages" onClick={() => basculer("messages")} />
        <Tuile n={nAvis} libelle="avis depuis lundi" ouverte={deplie === "avis"} testId="voy-tuile-avis" onClick={() => basculer("avis")} />
        <Tuile n={nSans} alerte={nSans > 0} libelle="sans réponse depuis 3 jours" ouverte={deplie === "sans"} testId="voy-tuile-sans-reponse" onClick={() => basculer("sans")} />
      </EnTete>
      {deplie ? (
        <div data-testid="voy-deplie" style={{ background: "#fff", padding: "0 24px 12px", borderBottom: `1px solid ${C.bord}` }}>
          {corps}
        </div>
      ) : null}
      <EtatLecture lecture={compteurs.etat === "echec" ? compteurs : null} quoi="Compteurs des messages" />
      <Bascule
        testId="voy-bascule"
        actif={tab}
        onChoisir={onTab}
        entrees={Object.entries(VOYAGEURS_ONGLETS).map(([id, libelle]) => ({
          id,
          libelle,
          ic: ICONES_VOY[id],
          nb:
            id === "messages" && Number.isFinite(nAttente) && nAttente > 0
              ? nAttente
              : id === "voyageurs-demandes" && Number.isFinite(nDemandes) && nDemandes > 0
                ? nDemandes
                : null,
        }))}
      />
    </div>
  );
}

function Fil({ f, actif, today, onClick }) {
  const s = ligneSejour(f.sejour, today);
  const pf = PLATEFORMES[f.plateforme] || PLATEFORMES.ferme;
  const tonS = s ? { vert: C.vertF, aub: C.aub, bleu: "#2B5AA8", gris: C.grisD }[s.ton] : C.gris;
  return (
    <button type="button" className={`nvoy-f${actif ? " on" : ""}`} onClick={onClick} data-testid={`voy-fil-${f.thread_id}`}>
      <span className="nvoy-av" aria-hidden="true">
        {initiales(f.voyageur)}
        <i style={{ background: pf.tx }}>{lettrePlateforme(f.plateforme)}</i>
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="nvoy-l1">
          <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: f.non_lus > 0 ? C.aub : "#CDD4D1", flexShrink: 0 }} />
          <b>{f.voyageur || "Voyageur"}</b>
          {f.a_traiter ? <span className="nvoy-urg" title="Le copilote a passé la main : à traiter.">URGENT</span> : null}
          <span className="d">{quand(f.dernier_a, today)}</span>
        </span>
        <span className="nvoy-s" style={{ display: "block" }}>
          {f.logement ? f.logement.nom : "Logement inconnu"}
          {s ? <span style={{ color: tonS, marginLeft: 6 }}>{s.sens} {s.texte}</span> : null}
        </span>
        {f.non_lus > 0 ? <span className="nvoy-s" style={{ display: "block", textAlign: "right", fontWeight: 700, color: C.encre }}>• {f.non_lus}</span> : null}
      </span>
    </button>
  );
}

function Apercu({ fil, onOuvrir }) {
  const [lecture, relire] = useLecture("ecran_fil_lire", { thread_id: fil.thread_id });
  const pf = PLATEFORMES[fil.plateforme] || PLATEFORMES.ferme;
  return (
    <div className="nvoy-droite" data-testid="voy-apercu">
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <b style={{ fontSize: 15 }}>{fil.voyageur || "Voyageur"}</b>
        <span data-testid="voy-plateforme" style={{ fontSize: 9.5, fontWeight: 700, background: pf.fond, color: pf.tx, borderRadius: 5, padding: "2px 6px", textTransform: "uppercase" }}>{PLATEFORMES[fil.plateforme] ? pf.nom : "—"}</span>
        {fil.sejour && fil.sejour.statut ? <span style={{ fontSize: 9.5, fontWeight: 700, background: C.vert, color: "#fff", borderRadius: 5, padding: "2px 6px", textTransform: "uppercase" }}>{/cancel|annul/i.test(fil.sejour.statut) ? "annulée" : "confirmée"}</span> : null}
      </div>
      <p className="nvoy-s">{fil.logement ? fil.logement.nom : "Logement inconnu"}</p>
      <div style={{ background: pf.fond, borderRadius: 12, padding: 12, marginTop: 10 }}>
        <EtatLecture lecture={lecture} quoi="Le fil" onRelire={relire} />
        {lecture.etat === "ok"
          ? (lecture.data.messages || []).map((m) => (
              <div key={m.id} style={{ display: "flex", justifyContent: m.de === "nous" ? "flex-end" : "flex-start" }}>
                <div className="nvoy-bulle" style={m.de === "nous" ? { background: "#17211E", color: "#fff" } : { background: "#fff" }}>
                  {m.texte}
                  <div style={{ fontSize: 10, opacity: 0.7, marginTop: 3 }}>{m.quand ? quand(m.quand, aujourdhuiParis()) : ""}</div>
                </div>
              </div>
            ))
          : null}
      </div>
      {lecture.etat === "ok" && lecture.data.curseur_suivant ? (
        <p className="nest-manque" data-testid="voy-fil-suite">Les messages plus anciens sont dans la conversation.</p>
      ) : null}
      <button type="button" className="nest-bt p" style={{ marginTop: 8 }} onClick={onOuvrir}>Ouvrir la conversation ›</button>
    </div>
  );
}

/** « Messages » : la liste des conversations et l'aperçu du fil choisi. */
export function VueMessages({ onOuvrirConversation, filInitial }) {
  const today = useMemo(() => aujourdhuiParis(), []);
  const [lecture, relire] = useLecture("ecran_conversations_lire", {});
  const [logements] = useLecture("ecran_logements_lire", {});
  const [f, setF] = useState({ filtre: "tous", texte: "", logement: "" });
  const [choisi, setChoisi] = useState(filInitial || null);
  // Une conversation demandée depuis l'en-tête (écran déjà ouvert) se choisit aussi.
  useEffect(() => {
    if (filInitial) setChoisi(filInitial);
  }, [filInitial]);
  const fils = lecture.etat === "ok" ? lecture.data.fils || [] : [];
  const cpt = lecture.etat === "ok" ? lecture.data.compteurs || {} : {};
  const visibles = filtrerFils(fils, f, today);
  const fil = fils.find((x) => x.thread_id === choisi) || null;
  const segs = [
    ["tous", "Tous", null],
    ["non_lus", "• Non lus", cpt.fils_non_lus],
    ["a_traiter", "• À traiter", cpt.a_traiter],
    ["aujourdhui", "Aujourd'hui", null],
  ];
  return (
    <div className="nest" data-testid="voy-messages">
      <StyleNest />
      <style>{CSS}</style>
      <div className="nest-page">
        <div className="nvoy-barre" style={{ borderRadius: "14px 14px 0 0", border: `1px solid ${C.bord}`, borderBottom: "none" }}>
          <input className="nest-champ" placeholder="Rechercher (voyageur, logement, texte)…" value={f.texte} onChange={(e) => setF((x) => ({ ...x, texte: e.target.value }))} />
          <span className="nest-seg">
            {segs.map(([k, t, nb]) => (
              <button key={k} type="button" className={f.filtre === k ? "on" : ""} onClick={() => setF((x) => ({ ...x, filtre: k }))}>
                {t}
                {Number.isFinite(nb) && nb > 0 ? <span style={{ fontSize: 10.5, fontWeight: 600, background: C.aubP, color: C.aubTx, borderRadius: 9, padding: "0 5px" }}>{nb}</span> : null}
              </button>
            ))}
          </span>
          <select className="nest-sel" aria-label="Logement" value={f.logement} onChange={(e) => setF((x) => ({ ...x, logement: e.target.value }))}>
            <option value="">Tous les logements</option>
            {logements.etat === "ok" ? logements.data.logements.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>) : null}
          </select>
        </div>
        <EtatLecture lecture={lecture} quoi="Les conversations" onRelire={relire} />
        <EtatLecture lecture={logements.etat === "echec" ? logements : null} quoi="Le filtre des logements" />
        {lecture.etat === "ok" && lecture.data.hors_fenetre > 0 ? (
          <p className="nest-manque" data-testid="voy-hors-fenetre">
            {pl(lecture.data.hors_fenetre, "conversation close", "conversations closes")} de plus de {lecture.data.fenetre_jours || 90} jours ne {lecture.data.hors_fenetre > 1 ? "sont" : "est"} pas listée{lecture.data.hors_fenetre > 1 ? "s" : ""}.
          </p>
        ) : null}
        {lecture.etat === "ok" ? (
          <div className="nvoy-g">
            <div className="nvoy-liste">
              {!visibles.length ? <p className="nest-etat">{fils.length ? "Aucune conversation pour ce filtre." : "Aucune conversation."}</p> : null}
              {visibles.map((x) => (
                <Fil key={x.thread_id} f={x} actif={x.thread_id === choisi} today={today} onClick={() => setChoisi(x.thread_id)} />
              ))}
            </div>
            {fil ? (
              <Apercu key={fil.thread_id} fil={fil} onOuvrir={() => onOuvrirConversation && onOuvrirConversation(fil.thread_id)} />
            ) : (
              <div className="nvoy-droite"><p className="nest-etat">Choisissez une conversation.</p></div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
